import { resolveWallRewardClearSpeed } from "./wallRewardClearSpeed";
import type { WallDefinition } from "./wallDefinitions";
import { FRESHNESS_WALL_BALANCE_PROFILE } from "./wallBalanceProfiles";
import {
  DEFAULT_WALL_REWARD_CAP_LEVEL,
  type WallRewardCapLevel,
} from "./wallRewardCap";

export const FRESHNESS_WALL_DEFINITION: WallDefinition = {
  id: "freshness-wall",
  firstClearReward: FRESHNESS_WALL_BALANCE_PROFILE.firstClearReward,
};

// Freshness Wall — Wall3. While active, MHov preview/execution growth is
// staled by the elapsed time since the last Hov-run boundary. The wall does
// not produce a hov-gain or simultaneous-purchase multiplier; instead its
// clear reward composes additively into the MHOV_REWARD_EXPONENT.
export const FRESHNESS_WALL_STALE_DURATION_MS =
  FRESHNESS_WALL_BALANCE_PROFILE.challenge.mHovStaleDurationMs;
export const FRESHNESS_WALL_STALE_FLOOR_EXPONENT =
  FRESHNESS_WALL_BALANCE_PROFILE.challenge.mHovStaleFloorExponent;
export const FRESHNESS_WALL_NHOV_STALE_DURATION_MS =
  FRESHNESS_WALL_BALANCE_PROFILE.challenge.nHovStaleDurationMs;
export const FRESHNESS_WALL_NHOV_STALE_FLOOR_EXPONENT =
  FRESHNESS_WALL_BALANCE_PROFILE.challenge.nHovStaleFloorExponent;
export const FRESHNESS_WALL_REWARD_TARGET_CLEAR_MS =
  FRESHNESS_WALL_BALANCE_PROFILE.reward.targetClearMs;
export const FRESHNESS_WALL_REWARD_EXPONENT_BONUS_BASE =
  FRESHNESS_WALL_BALANCE_PROFILE.reward.baseExponentBonus;
export const FRESHNESS_WALL_REWARD_EXPONENT_BONUS_MIN =
  FRESHNESS_WALL_BALANCE_PROFILE.reward.minimumExponentBonus;
export const FRESHNESS_WALL_REWARD_BASE_CAP_EXPONENT_BONUS =
  FRESHNESS_WALL_BALANCE_PROFILE.reward.caps.base;
export const FRESHNESS_WALL_REWARD_RELAXED_CAP_EXPONENT_BONUS =
  FRESHNESS_WALL_BALANCE_PROFILE.reward.caps.relaxed;
export const FRESHNESS_WALL_REWARD_EXPONENT_BONUS_FALLOFF =
  FRESHNESS_WALL_BALANCE_PROFILE.reward.falloff;

export function resolveFreshnessWallRewardEffectiveMaxExponentBonus(
  capLevel: WallRewardCapLevel = DEFAULT_WALL_REWARD_CAP_LEVEL,
): number {
  return capLevel === "relaxed"
    ? FRESHNESS_WALL_REWARD_RELAXED_CAP_EXPONENT_BONUS
    : FRESHNESS_WALL_REWARD_BASE_CAP_EXPONENT_BONUS;
}

export function resolveFreshnessWallRewardExponentBonus(
  bestClearMs: number | null,
  effectiveMaxExponentBonus: number = resolveFreshnessWallRewardEffectiveMaxExponentBonus(),
): number {
  if (bestClearMs === null) return 0;
  if (!Number.isFinite(bestClearMs) || bestClearMs <= 0) {
    return effectiveMaxExponentBonus;
  }
  const clearSpeed = resolveWallRewardClearSpeed(
    bestClearMs,
    FRESHNESS_WALL_REWARD_TARGET_CLEAR_MS,
    FRESHNESS_WALL_BALANCE_PROFILE.reward.softcapClearMs,
  );
  const raw =
    FRESHNESS_WALL_REWARD_EXPONENT_BONUS_BASE *
    clearSpeed ** FRESHNESS_WALL_REWARD_EXPONENT_BONUS_FALLOFF;
  if (!Number.isFinite(raw)) {
    return effectiveMaxExponentBonus;
  }
  return Math.min(
    effectiveMaxExponentBonus,
    Math.max(FRESHNESS_WALL_REWARD_EXPONENT_BONUS_MIN, raw),
  );
}

export function resolveFreshnessStalingExponent(
  elapsedSinceHovRunMs: number,
): number {
  if (!Number.isFinite(elapsedSinceHovRunMs) || elapsedSinceHovRunMs <= 0) {
    return 1;
  }

  const t = Math.min(
    1,
    elapsedSinceHovRunMs / FRESHNESS_WALL_STALE_DURATION_MS,
  );

  const raw = FRESHNESS_WALL_STALE_FLOOR_EXPONENT ** t;

  return Math.max(FRESHNESS_WALL_STALE_FLOOR_EXPONENT, raw);
}

export function resolveFreshnessWallNHovStalingExponent(
  elapsedSinceHovRunMs: number,
): number {
  if (!Number.isFinite(elapsedSinceHovRunMs) || elapsedSinceHovRunMs <= 0) {
    return 1;
  }
  const t = Math.min(
    1,
    elapsedSinceHovRunMs / FRESHNESS_WALL_NHOV_STALE_DURATION_MS,
  );
  const raw = FRESHNESS_WALL_NHOV_STALE_FLOOR_EXPONENT ** t;
  if (!Number.isFinite(raw)) return 1;
  return Math.max(FRESHNESS_WALL_NHOV_STALE_FLOOR_EXPONENT, raw);
}
